import { JOB_CONFIGS } from '@dev-console/job-config';
import type { JobLogEntry } from '@dev-console/types';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { EmptyState, ErrorBanner } from '@/components/ui/state-views';
import { searchJobLogs } from '@/config/api-helpers';
import { useSearchParamsState } from '@/hooks/useSearchParamsState';
import { formatTimestamp, LOG_LEVEL_COLORS, truncate } from '@/lib/utils';

const allJobNames = Array.from(new Set(JOB_CONFIGS.flatMap((c) => [...c.onDemandJobs, ...c.scheduledJobs]))).sort();
const allCanonicalJobs = JOB_CONFIGS.map((c) => c.canonicalJob).sort();
const logLevels = Object.keys(LOG_LEVEL_COLORS);

const TIME_RANGE_PRESETS = [
  { label: 'Last 15 minutes', ms: 15 * 60 * 1000 },
  { label: 'Last 1 hour', ms: 60 * 60 * 1000 },
  { label: 'Last 6 hours', ms: 6 * 60 * 60 * 1000 },
  { label: 'Last 24 hours', ms: 24 * 60 * 60 * 1000 },
  { label: 'Last 3 days', ms: 3 * 24 * 60 * 60 * 1000 },
  { label: 'Last 7 days', ms: 7 * 24 * 60 * 60 * 1000 },
] as const;

const toLocalDatetime = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const searchParamDefs = {
  traceId: { default: '' },
  canonicalJob: { default: '' },
  environment: {
    default: 'production' as 'production' | 'staging',
    parse: (raw: string): 'production' | 'staging' => (raw === 'staging' ? 'staging' : 'production'),
  },
  jobName: { default: '' },
  level: {
    default: '',
    parse: (raw: string): string => (logLevels.includes(raw) ? raw : ''),
  },
  timeRange: { default: 24 * 60 * 60 * 1000 },
  startTime: { default: '' },
  endTime: { default: '' },
};

export const JobSearch = () => {
  const {
    traceId: [traceIdFilter, setTraceIdFilter],
    canonicalJob: [canonicalJobFilter, setCanonicalJobFilter],
    environment: [environment, setEnvironment],
    jobName: [jobNameFilter, setJobNameFilter],
    level: [levelFilter, setLevelFilter],
    timeRange: [timeRangePreset, setTimeRangePreset],
    startTime: [customStartTime, setCustomStartTime],
    endTime: [customEndTime, setCustomEndTime],
  } = useSearchParamsState(searchParamDefs);
  const [results, setResults] = useState<JobLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const isCustomRange = timeRangePreset === 0;

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setExpandedIndex(null);
    try {
      let startTime: string | undefined;
      let endTime: string | undefined;

      if (isCustomRange) {
        if (customStartTime) startTime = new Date(customStartTime).toISOString();
        if (customEndTime) endTime = new Date(customEndTime).toISOString();
      } else {
        const now = new Date();
        startTime = new Date(now.getTime() - timeRangePreset).toISOString();
        endTime = now.toISOString();
      }

      const data = await searchJobLogs({
        traceId: traceIdFilter.trim() || undefined,
        canonicalJob: canonicalJobFilter.trim() || undefined,
        environment,
        jobName: jobNameFilter.trim() || undefined,
        level: levelFilter || undefined,
        startTime,
        endTime,
      });
      setResults(Array.isArray(data) ? data : data.logs ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [
    traceIdFilter,
    canonicalJobFilter,
    environment,
    jobNameFilter,
    levelFilter,
    isCustomRange,
    timeRangePreset,
    customStartTime,
    customEndTime,
  ]);

  //
  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (!didAutoSearch.current && traceIdFilter.trim()) {
      didAutoSearch.current = true;
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <div>
      {/* Filters */}
      <div className='mb-4 rounded-md border border-neutral-200 bg-neutral-50 p-4'>
        <div className='mb-3 flex flex-wrap items-center justify-between gap-3 border-b border-neutral-100 pb-3'>
          <div>
            <div className='text-sm font-medium text-neutral-900'>Worker trace search</div>
            <div className='mt-0.5 text-xs text-neutral-500'>Axiom worker-logs filtered by app environment.</div>
          </div>
          <div className='inline-flex rounded-md border border-neutral-200 bg-white p-0.5'>
            {(['production', 'staging'] as const).map((option) => (
              <button
                key={option}
                aria-pressed={environment === option}
                className={`rounded px-3 py-1 text-xs font-medium capitalize ${
                  environment === option
                    ? 'bg-neutral-900 text-white'
                    : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                }`}
                onClick={() => setEnvironment(option)}
                type='button'
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        <div className='grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3'>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Trace ID</label>
            <input
              type='text'
              value={traceIdFilter}
              onChange={(e) => setTraceIdFilter(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Enter trace ID...'
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            />
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Canonical Job</label>
            <input
              type='text'
              list='canonical-job-options'
              value={canonicalJobFilter}
              onChange={(e) => setCanonicalJobFilter(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Type to search...'
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            />
            <datalist id='canonical-job-options'>
              {allCanonicalJobs.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Job Name</label>
            <input
              type='text'
              list='job-name-options'
              value={jobNameFilter}
              onChange={(e) => setJobNameFilter(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder='Type to search...'
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            />
            <datalist id='job-name-options'>
              {allJobNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
          <div>
            <label htmlFor='job-search-level' className='mb-1 block text-xs font-medium text-neutral-600'>
              Level
            </label>
            <select
              id='job-search-level'
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            >
              <option value=''>All levels</option>
              {logLevels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='mb-1 block text-xs font-medium text-neutral-600'>Time Range</label>
            <select
              value={timeRangePreset}
              onChange={(e) => {
                const val = Number(e.target.value);
                setTimeRangePreset(val);
                if (val === 0 && !customStartTime) {
                  const now = new Date();
                  setCustomStartTime(toLocalDatetime(new Date(now.getTime() - 24 * 60 * 60 * 1000)));
                  setCustomEndTime(toLocalDatetime(now));
                }
              }}
              className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            >
              {TIME_RANGE_PRESETS.map((preset) => (
                <option key={preset.ms} value={preset.ms}>
                  {preset.label}
                </option>
              ))}
              <option value={0}>Custom range...</option>
            </select>
          </div>
          {isCustomRange && (
            <>
              <div>
                <label className='mb-1 block text-xs font-medium text-neutral-600'>Start Time</label>
                <input
                  type='datetime-local'
                  value={customStartTime}
                  onChange={(e) => setCustomStartTime(e.target.value)}
                  className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                />
              </div>
              <div>
                <label className='mb-1 block text-xs font-medium text-neutral-600'>End Time</label>
                <input
                  type='datetime-local'
                  value={customEndTime}
                  onChange={(e) => setCustomEndTime(e.target.value)}
                  className='w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                />
              </div>
            </>
          )}
        </div>
        <div className='mt-3'>
          <button
            onClick={handleSearch}
            disabled={loading}
            className='rounded-md bg-neutral-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error != null && <ErrorBanner className='mb-4' message={error} onRetry={handleSearch} />}

      {/* Results */}
      {!loading && hasSearched && error == null && results.length === 0 && (
        <EmptyState message='No log entries found matching your filters.' />
      )}

      {results.length > 0 && (
        <div className='overflow-x-auto rounded-md border border-neutral-200'>
          <table className='w-full text-left text-sm'>
            <thead className='border-b border-neutral-200 bg-neutral-50'>
              <tr>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Timestamp</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Canonical Job</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Job Name</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Trace ID</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Level</th>
                <th className='px-3 py-2.5 font-medium text-neutral-700'>Message</th>
              </tr>
            </thead>
            <tbody>
              {results.map((log, index) => {
                const logTraceId = (log.context?.traceId as string) ?? '';
                const logCanonicalJob = (log.context?.canonicalJob as string) ?? '—';
                const logJobName = (log.context?.job as string) ?? (log.context?.jobName as string) ?? '—';
                const colors = LOG_LEVEL_COLORS[log.level] ?? LOG_LEVEL_COLORS.info;
                const isExpanded = expandedIndex === index;

                return (
                  <Fragment key={index}>
                    <tr
                      onClick={() => setExpandedIndex(isExpanded ? null : index)}
                      className={`cursor-pointer border-b border-neutral-100 hover:bg-neutral-50 ${
                        isExpanded ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className='whitespace-nowrap px-3 py-2.5 text-xs text-neutral-600'>
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className='px-3 py-2.5 font-mono text-xs text-neutral-800'>{logCanonicalJob}</td>
                      <td className='px-3 py-2.5 font-mono text-xs text-neutral-800'>{logJobName}</td>
                      <td className='px-3 py-2.5 font-mono text-xs text-neutral-600'>
                        {logTraceId ? truncate(logTraceId, 16) : '—'}
                      </td>
                      <td className='px-3 py-2.5'>
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${colors.badge}`}>
                          {log.level}
                        </span>
                      </td>
                      <td className='max-w-xs px-3 py-2.5 text-neutral-600'>
                        <span className='block truncate'>{truncate(log.message, 80)}</span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className='border-b border-neutral-100 bg-neutral-50'>
                        <td colSpan={6} className='px-6 py-4'>
                          <div className='space-y-3'>
                            {/* Full message */}
                            <div>
                              <h4 className='mb-1 text-xs font-medium text-neutral-500'>Message</h4>
                              <p className='whitespace-pre-wrap text-sm text-neutral-800'>{log.message}</p>
                            </div>
                            {/* Metadata */}
                            <div className='flex flex-wrap gap-4 text-xs text-neutral-600'>
                              <span>
                                <span className='font-medium text-neutral-500'>Job:</span> {logJobName}
                              </span>
                              <span>
                                <span className='font-medium text-neutral-500'>Canonical:</span>{' '}
                                {(log.context?.canonicalJob as string) ?? '—'}
                              </span>
                              {logTraceId && (
                                <span>
                                  <span className='font-medium text-neutral-500'>Trace ID:</span>{' '}
                                  <code className='font-mono'>{logTraceId}</code>
                                </span>
                              )}
                            </div>
                            {/* Context JSON */}
                            <div>
                              <h4 className='mb-1 text-xs font-medium text-neutral-500'>Context</h4>
                              <pre className='max-h-80 overflow-auto rounded-md bg-neutral-900 p-4 text-xs text-neutral-100'>
                                {JSON.stringify(log.context, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!hasSearched && <EmptyState message='Enter filters and click Search to find job logs.' />}
    </div>
  );
};
